import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Activity } from 'lucide-react';

// Animated counter hook
const useCounter = (target, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  useEffect(() => {
    let startTime;
    let animationFrame;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(start + (target - start) * easeOut);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration, start]);
  return count;
};

// Mini sparkline chart
const Sparkline = ({ color = '#B8923F', data }) => {
  const points = data || [20, 35, 25, 45, 40, 60, 55, 75, 70, 85];
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const id = `grad-${color.replace('#', '')}`;
  
  const pathData = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * 100;
      const y = 100 - ((p - min) / range) * 80;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${pathData} L 100 100 L 0 100 Z`} fill={`url(#${id})`} />
      <path d={pathData} stroke={color} strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </svg>
  );
};

// Floating dashboard card
const DashboardCard = ({ children, className = '', delay = 0, floatY = -8, floatDuration = 6 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay }}
    className={`absolute ${className}`}
  >
    <motion.div
      animate={{ y: [0, floatY, 0] }}
      transition={{ duration: floatDuration, repeat: Infinity, ease: 'easeInOut' }}
      className="dark-card rounded-2xl p-5"
    >
      {children}
    </motion.div>
  </motion.div>
);

export const Hero3D = () => {
  const roas = useCounter(347, 2200);
  
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden" data-testid="hero-3d-canvas">
      {/* Subtle grid background */}
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Soft radial vignette */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, transparent 0%, rgba(10, 10, 10, 0.5) 70%, rgba(10, 10, 10, 0.95) 100%)'
      }} />
      
      {/* Decorative gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full opacity-[0.08]" style={{
        background: 'radial-gradient(circle, rgba(184, 146, 63, 1) 0%, transparent 70%)',
        filter: 'blur(80px)'
      }} />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.06]" style={{
        background: 'radial-gradient(circle, rgba(169, 61, 82, 1) 0%, transparent 70%)',
        filter: 'blur(80px)'
      }} />
      
      {/* LEFT: Single ROAS dashboard - sleek and prominent */}
      <DashboardCard 
        className="hidden lg:block top-[22%] left-[5%] w-60" 
        delay={0.6}
        floatY={-10}
        floatDuration={7}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/40">ROAS Promedio</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-gold pulse-dot" />
            <span className="font-mono text-[8px] tracking-widest text-gold">LIVE</span>
          </div>
        </div>
        <div className="font-display text-4xl font-light text-white tabular-nums leading-none mb-2">
          {roas.toFixed(0)}<span className="text-gold">%</span>
        </div>
        <div className="flex items-center gap-1.5 mb-4">
          <TrendingUp className="w-3 h-3 text-gold" />
          <span className="font-mono text-[10px] text-gold">+24.6%</span>
          <span className="font-mono text-[9px] text-white/30">vs mes anterior</span>
        </div>
        <div className="h-10 -mx-1">
          <Sparkline color="#B8923F" data={[30, 38, 35, 42, 40, 55, 48, 65, 72, 85]} />
        </div>
      </DashboardCard>

      {/* RIGHT: Single Markets Activity card - minimal */}
      <DashboardCard 
        className="hidden lg:block bottom-[22%] right-[5%] w-60"
        delay={0.9}
        floatY={10}
        floatDuration={8}
      >
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/40">Mercados Activos</span>
          <Activity className="w-3 h-3 text-burgundy-300" />
        </div>
        <div className="font-display text-4xl font-light text-white tabular-nums leading-none mb-4">
          12
        </div>
        <div className="space-y-2.5">
          {[
            { label: 'Europa', value: 60, color: '#B8923F' },
            { label: 'LATAM', value: 80, color: '#A93D52' },
          ].map((row) => (
            <div key={row.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[9px] tracking-widest uppercase text-white/40">{row.label}</span>
                <span className="font-mono text-[9px] text-white/60 tabular-nums">{row.value}%</span>
              </div>
              <div className="h-[3px] bg-white/[0.05] rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${row.value}%` }}
                  transition={{ duration: 1.5, delay: 1.2, ease: 'easeOut' }}
                  className="h-full rounded-full"
                  style={{ background: row.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </DashboardCard>

      {/* Subtle crosshair lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.08]">
        <line x1="50%" y1="0" x2="50%" y2="100%" stroke="rgba(184, 146, 63, 0.5)" strokeWidth="0.5" strokeDasharray="2 12" />
        <line x1="0" y1="50%" x2="100%" y2="50%" stroke="rgba(184, 146, 63, 0.5)" strokeWidth="0.5" strokeDasharray="2 12" />
      </svg>
    </div>
  );
};
